// src/pages/Catalog.tsx
//
// Public catalog — now server-driven from the real Postgres catalog
// (1 668 FileMaker products) via /api/products. Filters and search map
// to URL params; pagination is "load more" against the API.
//
// Admin/staff users see the same list PLUS per-row Hide buttons and
// (when a filter is active) a bulk-action bar that hides/unhides
// every result matching the current filter. Customer/anonymous users
// see exactly the same public surface as before — the hide UI is
// gated on `auth.isAuthenticated && isStaff()`.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, X, SlidersHorizontal, RefreshCcw, Eye, EyeOff, LayoutGrid, Tag, Heart } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { ProductCard } from "@/components/feature/ProductCard";
import { useAuth } from "@/components/auth/AuthContext";
import { cn } from "@/lib/cn";
import { Badge } from "@/components/ui/Badge";
import { isStaff } from "@/lib/auth";
import {
  fetchBrands,
  fetchProducts,
  fetchCategories,
  hideEntities,
  unhideEntities,
  mapApiProduct,
  setBrandVisibility,
  type BrandRow,
  type TaxCategory,
} from "@/lib/api";
import { brandLogoTileClass } from "@/lib/brandLogos";
import type { Product } from "@/lib/demoData";

const PAGE_SIZE = 60;

type SortKey = "name" | "price_asc" | "price_desc";
type ViewKey = "products" | "brands";

export function Catalog() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const query = params.get("q") ?? "";
  const category = params.get("category") ?? "";
  const catSlug = params.get("cat") ?? "";
  const brand = params.get("brand") ?? "";
  const sort = (params.get("sort") as SortKey | null) ?? "name";
  // Default to "brands" — the main /catalog surface is a directory, so
  // a brand-by-brand overview is the more useful landing. Power users
  // can switch to products with the toggle or by adding ?view=products.
  // Exception: if ?brand=X is set, the user is already drilling into a
  // single brand — the products view is the only useful one, so
  // override the default to "products" (still respects explicit
  // ?view=brands if the user navigates back via that URL).
  const viewParam = (params.get("view") as ViewKey | null);
  const view: ViewKey =
    viewParam ?? (brand ? "products" : "brands");

  const [searchInput, setSearchInput] = useState(query);
  const [showFilters, setShowFilters] = useState(false);

  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  // monotonically increasing id so late responses never overwrite newer ones
  const fetchId = useRef(0);

  // Admin-only bulk-action state. Tracks which product ids the
  // current user has hidden via the in-page action button so the
  // card label flips to "Show" without re-fetching the whole list.
  // The server is still the source of truth — on next reload the
  // /api/products public endpoint won't return the hidden row.
  const [hiddenProductIds, setHiddenProductIds] = useState<Set<string>>(
    new Set(),
  );
  // Same idea for brands: optimistic set of brand_ids the admin
  // has toggled in this session. The brand disappears from
  // /api/brands on next reload.
  const [hiddenBrandIds, setHiddenBrandIds] = useState<Set<number>>(
    new Set(),
  );
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<"hide" | "unhide" | null>(null);
  const [bulkError, setBulkError] = useState("");

  // Admin/staff users get the per-product Hide / Show action.
  // We rely on the backend's account_type -> is_staff mapping.
  const adminMode = auth.isReady && auth.isAuthenticated
    && isStaff(auth.accountType);

  // brand list for the filter dropdown
  useEffect(() => {
    fetchBrands()
      .then((j) => setBrands(j.rows))
      .catch(() => setBrands([]));
  }, []);

  // taxonomy tree for the cascading category filter (public endpoint)
  const [tax, setTax] = useState<TaxCategory[]>([]);
  useEffect(() => {
    fetchCategories()
      .then((j) => setTax(j.categories))
      .catch(() => setTax([]));
  }, []);

  const lang = (i18n.language || "fr").slice(0, 2);
  const taxLabel = (c: TaxCategory) =>
    (c.names[lang] || c.names.en || c.names.fr || c.slug) +
    (c.product_count ? ` (${c.product_count})` : "");
  // Selected path: walk up from the current slug to [top, mid, leaf].
  const byId = new Map(tax.map((c) => [c.id, c]));
  const catPath: TaxCategory[] = [];
  {
    let node = tax.find((c) => c.slug === catSlug);
    while (node) {
      catPath.unshift(node);
      node = node.parent_id != null ? byId.get(node.parent_id) : undefined;
    }
  }
  const taxChildren = (parentId: number | null) =>
    tax.filter((c) => c.parent_id === parentId);
  const catL1 = catPath.length >= 1 ? catPath[0] : undefined;
  const catL2 = catPath.length >= 2 ? catPath[1] : undefined;
  const setCatLevel = (level: number, slug: string) => {
    // clearing a level selects its parent (or clears at the top)
    const fallback = level > 0 ? catPath[level - 1]?.slug : undefined;
    updateParam("cat", slug || fallback || null);
  };

  // debounce the search box before it hits the URL param (and the API)
  useEffect(() => {
    const id = setTimeout(() => updateParam("q", searchInput || null), 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // main fetch: reset to first page whenever filters change
  useEffect(() => {
    const id = ++fetchId.current;
    setLoading(true);
    setError("");
    fetchProducts({
      q: query || undefined,
      category: category || undefined,
      cat: catSlug || undefined,
      brand: brand || undefined,
      sort,
      limit: PAGE_SIZE,
      offset: 0,
    })
      .then((page) => {
        if (id !== fetchId.current) return;
        setProducts(page.rows.map(mapApiProduct));
        setTotal(page.total);
      })
      .catch((e) => {
        if (id !== fetchId.current) return;
        setError(String(e));
        setProducts([]);
        setTotal(0);
      })
      .finally(() => {
        if (id === fetchId.current) setLoading(false);
      });
  }, [query, category, catSlug, brand, sort]);

  function loadMore() {
    const id = fetchId.current;
    setLoadingMore(true);
    fetchProducts({
      q: query || undefined,
      category: category || undefined,
      cat: catSlug || undefined,
      brand: brand || undefined,
      sort,
      limit: PAGE_SIZE,
      offset: products.length,
    })
      .then((page) => {
        if (id !== fetchId.current) return;
        setProducts((cur) => [...cur, ...page.rows.map(mapApiProduct)]);
        setTotal(page.total);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoadingMore(false));
  }

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const clearFilters = () => {
    setParams(new URLSearchParams());
    setSearchInput("");
  };

  // Drill into one brand: apply the brand filter AND switch back to
  // the products view so the user sees the actual list. Used by the
  // brand-card click AND the card's explicit "Products" button.
  const drillToBrand = (name: string) => {
    const next = new URLSearchParams(params);
    next.set("brand", name);
    next.delete("view");
    setParams(next);
  };

  // ----- admin: hide / show a single product -----
  async function toggleProductVisibility(p: Product) {
    if (!adminMode) return;
    const sku = p.sku;
    const isHiddenNow = hiddenProductIds.has(sku);
    setBusyIds((s) => new Set(s).add(sku));
    try {
      if (isHiddenNow) {
        await unhideEntities({
          entity_type: "product", scope: "selected", ids: [Number(sku)],
        });
        setHiddenProductIds((s) => {
          const next = new Set(s);
          next.delete(sku);
          return next;
        });
      } else {
        await hideEntities({
          entity_type: "product", scope: "selected", ids: [Number(sku)],
        });
        setHiddenProductIds((s) => new Set(s).add(sku));
      }
    } catch (e) {
      setBulkError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusyIds((s) => {
        const next = new Set(s);
        next.delete(sku);
        return next;
      });
    }
  }

  // ----- admin: hide / show a single brand -----
  async function toggleBrandVisibility(b: BrandRow) {
    if (!adminMode || b.brand_id == null) return;
    const bid = b.brand_id;
    const isHiddenNow = hiddenBrandIds.has(bid);
    setBusyIds((s) => new Set(s).add(`brand-${bid}`));
    try {
      await setBrandVisibility(bid, !isHiddenNow);
      setHiddenBrandIds((s) => {
        const next = new Set(s);
        if (isHiddenNow) next.delete(bid);
        else next.add(bid);
        return next;
      });
    } catch (e) {
      setBulkError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusyIds((s) => {
        const next = new Set(s);
        next.delete(`brand-${bid}`);
        return next;
      });
    }
  }

  // ----- admin: bulk hide/unhide matching the current filter -----
  async function bulkVisibility(action: "hide" | "unhide") {
    if (!adminMode) return;
    setBulkBusy(action);
    setBulkError("");
    try {
      const res = await (action === "hide"
        ? hideEntities({
            entity_type: "product",
            scope: "filter",
            filter: {
              q: query || undefined,
              category: category || undefined,
              brand: brand || undefined,
            },
          })
        : unhideEntities({
            entity_type: "product",
            scope: "filter",
            filter: {
              q: query || undefined,
              category: category || undefined,
              brand: brand || undefined,
            },
          }));
      setTotal(res.affected);
      // Refresh after a bulk change so the list matches the server.
      fetchId.current += 1;
      const newId = fetchId.current;
      setLoading(true);
      fetchProducts({
        q: query || undefined,
        category: category || undefined,
        brand: brand || undefined,
        sort,
        limit: PAGE_SIZE,
        offset: 0,
      })
        .then((page) => {
          if (newId !== fetchId.current) return;
          setProducts(page.rows.map(mapApiProduct));
          setTotal(page.total);
          // After a hide, optimistically mark all currently rendered rows
          // as hidden so the per-card label flips. The server will be
          // authoritative on the next full reload.
          if (action === "hide") {
            setHiddenProductIds((s) => {
              const next = new Set(s);
              for (const r of page.rows) next.add(r.product_id.toString());
              return next;
            });
          } else {
            setHiddenProductIds(new Set());
          }
        })
        .catch((e) => setError(String(e)))
        .finally(() => {
          if (newId === fetchId.current) setLoading(false);
        });
    } catch (e) {
      setBulkError(String(e instanceof Error ? e.message : e));
    } finally {
      setBulkBusy(null);
    }
  }

  const hasActiveFilters = !!(category || catSlug || brand || query);
  // Bulk-action bar: only when an admin has narrowed the results.
  // "Hide all results" without a filter would mean "hide every
  // product in the catalog" — destructive and surprising. We require
  // at least one active filter chip before exposing it.
  const bulkActionsEnabled = adminMode && hasActiveFilters;
  // Brand-level bulk action: when a brand filter is set, an admin
  // can hide the entire brand (cascades to all products of it).
  const brandFilterObj = useMemo(
    () => brands.find((b) => b.name === brand),
    [brands, brand],
  );
  const brandNames = useMemo(
    () => brands.map((b) => b.name)
      .sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" })),
    [brands]);

  return (
    <div className="container py-12">
      <header className="mb-8">
        <h1 className="text-3xl font-510 tracking-tight text-fg-primary sm:text-4xl">
          {t("catalog.title")}
        </h1>
        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-fg-tertiary">
          <span>
            {t("catalog.acrossBrands", "{{products}} products across {{brands}} brands", { products: total.toLocaleString(), brands: brands.length })}
          </span>
        </p>
      </header>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-quaternary" strokeWidth={1.5} />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("catalog.searchPlaceholder", "Search by name, brand, SKU, tag…")}
            className="pl-9"
          />
        </div>
        <Button
          variant={showFilters ? "secondary" : "ghost"}
          size="md"
          onClick={() => setShowFilters((s) => !s)}
          className="sm:self-auto"
        >
          <SlidersHorizontal className="h-4 w-4" strokeWidth={1.5} />
          {showFilters ? t("catalog.hideFilters", "Hide filters") : t("catalog.filters", "Filters")}
        </Button>
      </div>

      {showFilters && (
        <Card className="mb-6">
          <CardContent className="grid gap-4 p-4 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1.5 block text-xs font-510 uppercase tracking-wider text-fg-tertiary">
                {t("catalog.category", "Category")}
              </span>
              <select
                value={catPath[0]?.slug ?? ""}
                onChange={(e) => setCatLevel(0, e.target.value)}
                className="h-9 w-full rounded border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary focus:border-accent-indigo focus:outline-none"
              >
                <option value="">{t("catalog.all", "All")}</option>
                {taxChildren(null).map((c) => (
                  <option key={c.slug} value={c.slug}>{taxLabel(c)}</option>
                ))}
              </select>
            </label>
            {catL1 && taxChildren(catL1.id).length > 0 && (
              <label className="block">
                <span className="mb-1.5 block text-xs font-510 uppercase tracking-wider text-fg-tertiary">
                  {t("catalog.subcategory", "Subcategory")}
                </span>
                <select
                  value={catL2?.slug ?? ""}
                  onChange={(e) => setCatLevel(1, e.target.value)}
                  className="h-9 w-full rounded border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary focus:border-accent-indigo focus:outline-none"
                >
                  <option value="">{t("catalog.all", "All")}</option>
                  {taxChildren(catL1.id).map((c) => (
                    <option key={c.slug} value={c.slug}>{taxLabel(c)}</option>
                  ))}
                </select>
              </label>
            )}
            {catL2 && taxChildren(catL2.id).length > 0 && (
              <label className="block">
                <span className="mb-1.5 block text-xs font-510 uppercase tracking-wider text-fg-tertiary">
                  {t("catalog.subcategory", "Subcategory")}
                </span>
                <select
                  value={catPath.length >= 3 ? catPath[2]?.slug ?? "" : ""}
                  onChange={(e) => setCatLevel(2, e.target.value)}
                  className="h-9 w-full rounded border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary focus:border-accent-indigo focus:outline-none"
                >
                  <option value="">{t("catalog.all", "All")}</option>
                  {taxChildren(catL2.id).map((c) => (
                    <option key={c.slug} value={c.slug}>{taxLabel(c)}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="block">
              <span className="mb-1.5 block text-xs font-510 uppercase tracking-wider text-fg-tertiary">
                {t("catalog.byBrand", "Brand")}
              </span>
              <select
                value={brand}
                onChange={(e) => updateParam("brand", e.target.value || null)}
                className="h-9 w-full rounded border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary focus:border-accent-indigo focus:outline-none"
              >
                <option value="">{t("catalog.all", "All")}</option>
                {brandNames.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-510 uppercase tracking-wider text-fg-tertiary">
                {t("catalog.sort", "Sort by")}
              </span>
              <select
                value={sort}
                onChange={(e) => updateParam("sort", e.target.value || null)}
                className="h-9 w-full rounded border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary focus:border-accent-indigo focus:outline-none"
              >
                {/*
                  Brands view only supports alphabetical order (price
                  doesn't apply to brand cards). Hide price options
                  and lock the select to "name" when in brands view.
                */}
                {view === "brands" ? (
                  <option value="name">{t("catalog.sortName", "Name")}</option>
                ) : (
                  <>
                    <option value="name">{t("catalog.sortName", "Name")}</option>
                    <option value="price_asc">{t("catalog.sortPriceAsc", "Price ↑")}</option>
                    <option value="price_desc">{t("catalog.sortPriceDesc", "Price ↓")}</option>
                  </>
                )}
              </select>
            </label>
          </CardContent>
        </Card>
      )}

      {hasActiveFilters && (
        <div className="mb-3 flex items-center gap-2 text-xs text-fg-tertiary">
          <span>{t("catalog.activeFilters", "Active filters:")}</span>
          {query && (
            <button
              type="button"
              onClick={() => { setSearchInput(""); updateParam("q", null); }}
              className="inline-flex items-center gap-1 rounded bg-bg-elevated px-2 py-0.5 text-fg-secondary hover:bg-bg-hover"
            >
              “{query}” <X className="h-3 w-3" />
            </button>
          )}
          {category && (
            <button
              type="button"
              onClick={() => updateParam("category", null)}
              className="inline-flex items-center gap-1 rounded bg-bg-elevated px-2 py-0.5 text-fg-secondary hover:bg-bg-hover"
            >
              {category} <X className="h-3 w-3" />
            </button>
          )}
          {catSlug && (
            <button
              type="button"
              onClick={() => updateParam("cat", null)}
              className="inline-flex items-center gap-1 rounded bg-bg-elevated px-2 py-0.5 text-fg-secondary hover:bg-bg-hover"
            >
              {catPath.length > 0 ? taxLabel(catPath[catPath.length - 1]!) : catSlug}
              <X className="h-3 w-3" />
            </button>
          )}
          {brand && (
            <button
              type="button"
              onClick={() => updateParam("brand", null)}
              className="inline-flex items-center gap-1 rounded bg-bg-elevated px-2 py-0.5 text-fg-secondary hover:bg-bg-hover"
            >
              {brand} <X className="h-3 w-3" />
            </button>
          )}
          <button
            type="button"
            onClick={clearFilters}
            className="ml-auto text-benchmark-teal-bright hover:underline"
          >
            {t("catalog.clearFilters", "Clear all filters")}
          </button>
        </div>
      )}

      {bulkActionsEnabled && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-benchmark-teal/40 bg-benchmark-teal/5 p-3 text-xs">
          <span className="font-510 uppercase tracking-wider text-benchmark-teal-bright">
            Admin · bulk visibility
          </span>
          <span className="text-fg-tertiary">
            {total.toLocaleString()} result{total === 1 ? "" : "s"} match the current filter
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => bulkVisibility("hide")}
              disabled={bulkBusy !== null || total === 0}
              aria-label={`Hide all ${total} matching products from the public catalog`}
            >
              <EyeOff className="h-3 w-3" strokeWidth={1.5} />
              {bulkBusy === "hide" ? "Hiding…" : `Hide ${total.toLocaleString()} results`}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => bulkVisibility("unhide")}
              disabled={bulkBusy !== null || total === 0}
              aria-label={`Unhide all ${total} matching products`}
            >
              <Eye className="h-3 w-3" strokeWidth={1.5} />
              {bulkBusy === "unhide" ? "Unhiding…" : `Unhide ${total.toLocaleString()} results`}
            </Button>
            {brand && brandFilterObj?.brand_id != null && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => toggleBrandVisibility(brandFilterObj)}
                disabled={busyIds.has(`brand-${brandFilterObj.brand_id}`)}
                aria-label={
                  hiddenBrandIds.has(brandFilterObj.brand_id)
                    ? `Unhide the ${brand} brand`
                    : `Hide the ${brand} brand (and all its products)`
                }
              >
                <EyeOff className="h-3 w-3" strokeWidth={1.5} />
                {hiddenBrandIds.has(brandFilterObj.brand_id)
                  ? `Show brand “${brand}”`
                  : `Hide brand “${brand}”`}
              </Button>
            )}
          </div>
          {bulkError && (
            <p className="basis-full text-[11px] text-accent-rose">
              {bulkError}
            </p>
          )}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-fg-tertiary">
          {t("catalog.results", "{{count}} products", { count: total })}
        </p>
        <div
          role="tablist"
          aria-label={t("catalog.viewLabel", "View")}
          className="inline-flex rounded-md border border-border bg-bg-surface p-0.5"
        >
          <button
            type="button"
            role="tab"
            aria-selected={view === "products"}
            onClick={() => updateParam("view", "products")}
            className={cn(
              "inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-510 uppercase tracking-wider transition-colors",
              view === "products"
                ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                : "text-fg-tertiary hover:text-fg-primary",
            )}
          >
            <LayoutGrid className="h-3 w-3" strokeWidth={1.5} />
            {t("catalog.viewProducts", "Products")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "brands"}
            onClick={() => updateParam("view", "brands")}
            className={cn(
              "inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-510 uppercase tracking-wider transition-colors",
              view === "brands"
                ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                : "text-fg-tertiary hover:text-fg-primary",
            )}
          >
            <Tag className="h-3 w-3" strokeWidth={1.5} />
            {t("catalog.viewBrands", "Brands")}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          {t("catalog.apiError", "Catalog unavailable — is the API running?")} ({error})
        </div>
      )}

      {loading ? (
        <p className="py-12 text-center text-sm text-fg-tertiary">
          <RefreshCcw className="mr-2 inline h-4 w-4 animate-spin" />
          {t("catalog.loading", "Loading products…")}
        </p>
      ) : products.length === 0 && !error ? (
        <EmptyState
          title={t("catalog.empty", "No products match your filters")}
          description={t("catalog.emptyHint", "Try removing a filter or using a broader search query.")}
          action={
            hasActiveFilters ? (
              <Button variant="secondary" onClick={clearFilters}>
                {t("catalog.clearFilters", "Clear all filters")}
              </Button>
            ) : undefined
          }
        />
      ) : view === "brands" ? (
        // Brands view: use the FULL brand list from /api/brands (already
        // loaded into the `brands` state) so every brand is discoverable
        // — not just the brands that happen to appear on the current
        // product page. The per-card count is the actual server count
        // (col "count" on brands), not a local page slice. Logo comes
        // from /api/brands/logos/<slug>.svg with a monogram fallback.
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {brands.length === 0 ? (
            <p className="col-span-full py-8 text-center text-sm text-fg-tertiary">
              {t("catalog.noBrands", "No brands in the current result set.")}
            </p>
          ) : (
            brands
              // Sort alphabetically by name so the user can scan a
              // long list (currently 77 brands, grows with imports).
              // Case-insensitive, accent-insensitive.
              .sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }))
              // Filter the brand list by the current URL search/category
              // filters so the brand view stays consistent with the
              // products view. The brand filter itself is hidden in
              // brand view (it would be self-referential) so the card
              // count of any brand the user could click is the real one.
              .filter((b) => {
                if (query && !b.name.toLowerCase().includes(query.toLowerCase())) return false;
                return true;
              })
              .map((b) => {
                const monogram = b.name.trim().charAt(0).toUpperCase() || "?";
              return (
                <Card
                  key={b.name}
                  className="group cursor-pointer overflow-hidden transition-colors hover:border-benchmark-teal/40"
                  onClick={() => drillToBrand(b.name)}
                >
                  <CardContent className="space-y-3 p-5">
                    <div className="flex items-start gap-3">
                      <div className={brandLogoTileClass(b.logo_url)}>
                        {/* PLACEHOLDER until real logos land in
                            data/brand-logos/<slug>.svg. The endpoint
                            always returns 200 so this <img> never
                            404s. */}
                        {b.logo_url ? (
                          <img
                            src={b.logo_url}
                            alt={b.name}
                            width={80}
                            height={48}
                            loading="lazy"
                            className="h-full w-full object-contain"
                            onError={(e) => {
                              // If the slug-specific logo is missing
                              // server-side (placeholder still works
                              // but real assets may be 404 until the
                              // operator drops them in), fall back to
                              // the monogram by hiding this img.
                              (e.currentTarget as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : null}
                        <span
                          className="font-510 text-base text-fg-quaternary"
                          style={b.logo_url ? { display: "none" } : undefined}
                          aria-hidden
                        >
                          {monogram}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-sm font-510 text-fg-primary">
                          {b.name}
                        </h3>
                        <p className="mt-0.5 text-xs text-fg-tertiary">
                          {t("catalog.brandCardCount", "{{count}} products on this page", { count: b.count })}
                        </p>
                      </div>
                      <Badge size="sm">{b.count}</Badge>
                    </div>
                    <div className="border-t border-border pt-2">
                      {/* Explicit card actions (BenchMark-avPROD#11):
                          "Products" drills into the brand — same as the
                          card click, but a labelled button a tester can
                          find. "Favorites" opens the account's favorites
                          filtered to this brand; it is only rendered for
                          a signed-in (registered) user because favorites
                          do not exist for an anonymous visitor. */}
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="flex-1"
                          aria-label={t("catalog.brandCardProductsAria", "View {{name}} products", { name: b.name })}
                          onClick={(e) => {
                            e.stopPropagation();
                            drillToBrand(b.name);
                          }}
                        >
                          <LayoutGrid className="h-3 w-3" strokeWidth={1.5} />
                          {t("catalog.brandCardProducts", "Products")}
                        </Button>
                        {auth.isAuthenticated && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="flex-1"
                            aria-label={t("catalog.brandCardFavoritesAria", "View my {{name}} favorites", { name: b.name })}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/portal/favorites?brand=${encodeURIComponent(b.name)}`,
                              );
                            }}
                          >
                            <Heart className="h-3 w-3" strokeWidth={1.5} />
                            {t("catalog.brandCardFavorites", "Favorites")}
                          </Button>
                        )}
                      </div>
                      <p className="mt-1 text-[11px] text-fg-quaternary">
                        {t("catalog.brandCardHint", "Click to see only this brand")}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <ProductCard
                key={p.sku}
                product={p}
                actions={
                  adminMode ? (
                    <Button
                      size="sm"
                      variant={hiddenProductIds.has(p.sku) ? "secondary" : "ghost"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void toggleProductVisibility(p);
                      }}
                      disabled={busyIds.has(p.sku)}
                      aria-label={
                        hiddenProductIds.has(p.sku)
                          ? `Unhide ${p.name} from the public catalog`
                          : `Hide ${p.name} from the public catalog`
                      }
                      className="rounded-full bg-bg-surface/90 px-2 backdrop-blur"
                    >
                      {hiddenProductIds.has(p.sku) ? (
                        <Eye className="h-3 w-3" strokeWidth={1.5} />
                      ) : (
                        <EyeOff className="h-3 w-3" strokeWidth={1.5} />
                      )}
                    </Button>
                  ) : null
                }
              />
            ))}
          </div>
          {products.length < total && (
            <div className="mt-6 text-center">
              <Button variant="secondary" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? (
                  <RefreshCcw className="h-4 w-4 animate-spin" />
                ) : null}
                {t("catalog.loadMore", "Load more")} ({products.length} / {total.toLocaleString()})
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
